import React from "react";
import { useQuery } from "@tanstack/react-query";
import OopsPage from "@pageContent/OopsPage";
import Pagination from "@pageContent/Pagination";
import {
  InfiniteCollectionContainer,
  ItemsContainer,
  Title,
  TitleWrapper,
} from "@pageContent/common/InfiniteCollection/InfiniteCollection";
import InfiniteCollectionItem from "@pageContent/common/InfiniteCollection/InfiniteCollectionItem";
import { CollectionSkeleton } from "@pageContent/common/Skeletons/InfiniteCollectionSkeleton";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";
import * as Styled from "./PaginatedOverview.styled";

const noResultText = "Helaas kunnen we geen items vinden.";

type CollectionResponse = LinkList & { total: number };

const PaginatedOverview: React.FC<{
  category?: Pick<CategoryEntry, "articleEndpoint" | "videoEndpoint">;
  page: number;
  limit: number;
  collectionTitle: string;
  onPageChange?: (page: number) => void;
  isVideoOverview: boolean;
  activeId?: string;
  initialData?: CollectionResponse;
}> = ({ page, category, limit, collectionTitle, isVideoOverview, initialData, activeId, onPageChange }) => {
  const { articleEndpoint, videoEndpoint } = category || {};
  const skip = (page - 1) * limit;

  const { data, isLoading, error } = useQuery({
    queryKey: ["category-overview", isVideoOverview, videoEndpoint, articleEndpoint, skip, limit],
    queryFn: () =>
      fetchByEndpoint<CollectionResponse>(
        `${isVideoOverview ? videoEndpoint : articleEndpoint}?skip=${skip}&limit=${limit}`
      ),
    enabled: !!category,
    initialData,
  });

  const { items, total } = data || {};
  const count = total ? Math.ceil(total / limit) : 0;

  if (isLoading || !category)
    return (
      <InfiniteCollectionContainer>
        <TitleWrapper>
          <Title>{collectionTitle}</Title>
        </TitleWrapper>

        <ItemsContainer>
          <CollectionSkeleton title={collectionTitle} limit={limit} />
        </ItemsContainer>
      </InfiniteCollectionContainer>
    );

  if (!items) return <OopsPage error={error} />;

  return (
    <>
      <InfiniteCollectionContainer>
        <TitleWrapper>
          <Title>{collectionTitle}</Title>
        </TitleWrapper>
      </InfiniteCollectionContainer>
      {items.length ? (
        <Styled.Items>
          {items.map((item, index) => (
            <InfiniteCollectionItem
              key={item.id}
              position={index + 1}
              sectionTitle={collectionTitle}
              isActive={!!item.videoGuid && item.videoGuid === activeId}
              link={{ ...item, hasVideo: true }}
            />
          ))}
        </Styled.Items>
      ) : (
        <Styled.NoItemsText>{noResultText}</Styled.NoItemsText>
      )}

      {!!count && <Pagination count={count} currentPage={page} onPageChange={onPageChange} />}
    </>
  );
};
export default PaginatedOverview;
